"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useRef, type ComponentProps } from "react";

/**
 * A link that prefetches on INTENT — the pointer coming over it, a finger
 * landing on it, keyboard focus — instead of the moment it scrolls into view.
 *
 * The shell's links (rail, tab bar, top mark, footer) are on screen on every
 * page, so `Link`'s default viewport prefetch sent a dozen route requests on
 * every load (each link twice), all competing with the page's own fonts,
 * styles and hydration — and some answered 403 by Cloudflare's bot check,
 * which Lighthouse logs as console errors. Prefetching on intent keeps the
 * click as fast (the request starts ~100–300 ms before it) without paying
 * for routes nobody opens.
 */
export default function IntentLink({ href, onPointerEnter, onTouchStart, onFocus, ...rest }: ComponentProps<typeof Link>) {
  const router = useRouter();
  const done = useRef(false);
  const warm = () => {
    if (done.current || typeof href !== "string") return;
    done.current = true;
    router.prefetch(href);
  };
  return (
    <Link
      {...rest}
      href={href}
      prefetch={false}
      onPointerEnter={(e) => { warm(); onPointerEnter?.(e); }}
      onTouchStart={(e) => { warm(); onTouchStart?.(e); }}
      onFocus={(e) => { warm(); onFocus?.(e); }}
    />
  );
}
